'use client'
import debounce from 'lodash/debounce'
import Image from 'next/image'
import type { CSSProperties, FC } from 'react'
import { useEffect } from 'react'
import { useCallback } from 'react'
import { useMemo } from 'react'
import React, { useState } from 'react'
import styles from './AddressMap.module.scss'
import { isLocationInDeliveryArea } from '@/location/use-cases'
import { useAuth } from '@/tools/auth'
import { useLocalisation } from '@/tools/i18n/client'
import { getImageUrl } from '@/tools/image-service/client'
import { useErrorHandling } from '@/tools/o11y/client'
import { useAddressBookContext } from '@/user/address-book/AddressBookContext'
import { addressCheckEvent } from '@/user/address-book/analytics'
import type { AddressCoordinates, UserAddress, UserAddressNew } from '@/user/address-book/entities'
import type { GoogleCoordinates } from '@/user/address-book/types'
import { GenericMap } from '@/user/address-book/ui/components/generic-map/GenericMap'
import type { MapTooltipProps } from '@/user/address-book/ui/components/map-tooltip/MapTooltip'
import { MapTooltip } from '@/user/address-book/ui/components/map-tooltip/MapTooltip'
import { getAddressByCoordinates } from '@/user/address-book/use-cases'

const FETCH_ADDRESS_DEBOUNCE = 600

type Props = {
  style: CSSProperties
}

export const AddressMap: FC<Props> = ({ style }) => {
  const { t } = useLocalisation()
  const {
    navigate,
    currentStep,
    addressData,
    isInArea,
    setIsInArea,
    setAddress,
    setOriginalCoordinates,
    deviceCurrentLocation: { deviceLocation },
  } = useAddressBookContext()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [mapAddress, setMapAddress] = useState<UserAddress | UserAddressNew>(addressData)
  const { execWithErrorTracking } = useErrorHandling()
  const { isLoggedIn } = useAuth()

  const resetMapAddress = () => {
    setMapAddress({ ...mapAddress, latitude: 0, longitude: 0 })
  }

  const tooltipImgSrc = getImageUrl({
    source: 'dh:customer-assets-glovo/address_flow/address_book/bike_icon.svg',
  })

  const tooltip = useMemo(() => {
    if (!isLoading && !mapAddress?.longitude && !mapAddress?.latitude) {
      return null
    }

    const propsData: MapTooltipProps = {
      title: `${mapAddress.addressLine || ''}`,
      icon: (
        <Image
          alt="Bike icon"
          className={styles.bikeIcon}
          height={18}
          src={tooltipImgSrc}
          width={20}
        />
      ),
      isLoading,
      width: 310,
    }
    if (currentStep === 'FIND_ADDRESS_ON_MAP') {
      if (!isInArea) {
        propsData.title = t('address-list.out-of-delivery-area')
        propsData.description = t('address-list.we-cant-deliver-here')
      } else {
        propsData.actionText = t('address-list.use-this-point')
        propsData.onActionClick = () => {
          setAddress(mapAddress)
          setOriginalCoordinates(mapAddress)
          navigate('onLocationSelected')
        }
      }
      return <MapTooltip {...propsData} />
    }
    if (currentStep === 'ADDRESS_TYPE_FORM') {
      propsData.description = t('address-list.we-will-deliver-here')

      return <MapTooltip {...propsData} />
    }

    return null
  }, [
    mapAddress,
    setAddress,
    currentStep,
    navigate,
    isInArea,
    t,
    tooltipImgSrc,
    isLoading,
    setOriginalCoordinates,
  ])

  useEffect(() => {
    execWithErrorTracking(() => {
      setMapAddress(addressData)
      addressCheckEvent({
        address: addressData,
        addressStatusSuccessful: true,
        checkType: 'Map',
        query: null,
      })
    })
  }, [addressData, setMapAddress, execWithErrorTracking])

  const handleLocationChange = async (location: GoogleCoordinates) => {
    resetMapAddress()
    if (currentStep === 'WHERE_TO_DELIVER' || currentStep === 'ADDRESS_TYPE_FORM') {
      navigate('onMapInteraction')
    }
    await debouncedFetchingData({ latitude: location.lat, longitude: location.lng })
  }

  const handleDragstart = resetMapAddress

  const fetchAddressDetails = async (location: AddressCoordinates) => {
    setIsLoading(true)
    const isInArea = await isLocationInDeliveryArea(location)
    setIsInArea(isInArea)
    if (isInArea) {
      setMapAddress(await getAddressByCoordinates(location, isLoggedIn))
    } else {
      setMapAddress({ ...mapAddress, ...location })
    }
    setIsLoading(false)
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFetchingData = useCallback(
    debounce((query) => fetchAddressDetails(query), FETCH_ADDRESS_DEBOUNCE, {
      leading: false,
    }),
    []
  )

  const positionOverride = useMemo(
    () =>
      addressData.latitude || addressData.longitude
        ? {
            lat: addressData.latitude,
            lng: addressData.longitude,
          }
        : undefined,
    [addressData.latitude, addressData.longitude]
  )

  const userMarkPosition = useMemo(
    () =>
      deviceLocation?.coordinates
        ? {
            lat: deviceLocation.coordinates?.latitude,
            lng: deviceLocation.coordinates?.longitude,
          }
        : undefined,
    [deviceLocation]
  )

  return (
    <div className={styles.addressMap} style={style}>
      <GenericMap
        isOutOfZone={!isInArea}
        mapId={'main-address-map'}
        positionOverride={positionOverride}
        tooltip={tooltip}
        userMarkPosition={userMarkPosition}
        onDragStart={handleDragstart}
        onLocationChange={handleLocationChange}
      />
    </div>
  )
}
